import { useCallback, useMemo, useState } from "react";
import { Button, Breadcrumb, Spin, Segmented, Input } from "antd";
import {
  PlusOutlined,
  HomeOutlined,
  BarsOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import AddEditModal from "./Modal/AddEditModal";
import { useTenantSpreadsheet } from "../../../hooks/useTenantSpreadsheet";
import { StyledSpreadsheet } from "./styles";
import ContentHeader from "../../../components/shared/ContentHeader";
import { useDispatch, useSelector } from "react-redux";
import { setKeyValue } from "../../../store/spreadsheet/slice";
import GridView from "./GridView";
import ListView from "./ListView";
import { useTenantUsers } from "../../../hooks/useTenantUsers";
import { selectUser } from "../../../store/user/selectors";
import RemoveModal from "./Modal/RemoveModal";

const Spreadsheet = () => {
  useTenantUsers();

  const { folders, loading, selectedFolder } = useTenantSpreadsheet();
  const { user_id } = useSelector(selectUser);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isGridView, setIsGridView] = useState(true);

  const dispatch = useDispatch();

  const folderList = useMemo(() => {
    return Object.values(folders || {}).filter(
      (folder) =>
        !!folder?.name &&
        folder?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
    );
  }, [folders, searchTerm]);

  const files = useMemo(
    () =>
      (selectedFolder?.workbooks || folders?.["null"]?.workbooks || [])?.filter(
        ({ name = "" }) =>
          name.toLowerCase().includes(searchTerm?.toLowerCase()),
      ),
    [selectedFolder, folders, searchTerm],
  );

  const onFolderSelect = useCallback((id) => {
    dispatch(
      setKeyValue({
        key: "selectedFolderId",
        value: id,
      }),
    );
  }, []);

  return (
    <StyledSpreadsheet>
      <ContentHeader
        title={
          <Breadcrumb
            items={[
              {
                title: (
                  <>
                    <HomeOutlined />
                    <span>Home</span>
                  </>
                ),
                onClick: () => onFolderSelect(null),
              },
              ...(selectedFolder
                ? [
                    {
                      title: selectedFolder?.name,
                    },
                  ]
                : []),
            ]}
          />
        }
      >
        <Input
          placeholder="Search..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Segmented
          options={[
            {
              label: <AppstoreOutlined />,
              value: true,
            },
            {
              label: <BarsOutlined />,
              value: false,
            },
          ]}
          onChange={(value) => setIsGridView(value)}
          block
          value={isGridView}
        />

        <Button
          type="primary"
          className="add-btn"
          icon={<PlusOutlined />}
          onClick={() => setSelectedRecord({})}
        />
      </ContentHeader>

      {loading ? (
        <div className="fm-loading flex-center">
          <Spin size="large" />
        </div>
      ) : isGridView ? (
        <GridView
          selectedFolder={selectedFolder}
          folderList={folderList}
          onFolderSelect={onFolderSelect}
          files={files}
          user_id={user_id}
          setSelectedRecord={setSelectedRecord}
        />
      ) : (
        <ListView
          selectedFolder={selectedFolder}
          folderList={folderList}
          onFolderSelect={onFolderSelect}
          files={files}
          user_id={user_id}
          setSelectedRecord={setSelectedRecord}
        />
      )}
      {!!selectedRecord &&
        (selectedRecord?.isDelete ? (
          <RemoveModal
            record={selectedRecord}
            onCancel={() => setSelectedRecord(null)}
            folder_id={selectedFolder?.id || null}
          />
        ) : (
          <AddEditModal
            onCancel={() => setSelectedRecord(null)}
            selectedRecord={selectedRecord}
            folder_id={selectedFolder?.id || null}
          />
        ))}
    </StyledSpreadsheet>
  );
};

export default Spreadsheet;
